import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ServiceCTAProps {
  serviceName: string;
  description?: string;
  buttonText?: string;
  badgeText?: string;
  placeholderText?: string;
  source?: string;
}

const serviceConfigs = {
  "AI Agents": {
    description: "Transform your business with intelligent automation that works 24/7",
    buttonText: "Automate My Business",
    badgeText: "Intelligent Automation",
    placeholderText: "Tell us about your business processes you'd like to automate..."
  },
  "Analytics": {
    description: "Unlock insights that drive growth and informed decision-making",
    buttonText: "Get Data Insights",
    badgeText: "Data-Driven Growth",
    placeholderText: "What business metrics would you like to track and improve..."
  },
  "Web Development": {
    description: "Build websites that convert visitors into loyal customers",
    buttonText: "Build My Website",
    badgeText: "High-Converting Websites",
    placeholderText: "Describe your ideal website and business goals..."
  },
  "UX/UI Design": {
    description: "Create experiences users love and can't stop talking about",
    buttonText: "Design My Experience",
    badgeText: "User-Centered Design",
    placeholderText: "Tell us about your design vision and user needs..."
  },
  "Data Integration": {
    description: "Connect your systems seamlessly for unified data flow",
    buttonText: "Integrate My Systems",
    badgeText: "Seamless Integration",
    placeholderText: "What systems do you need to connect and integrate..."
  },
  "Workflow Automation": {
    description: "Automate repetitive tasks and boost team productivity",
    buttonText: "Automate My Workflow",
    badgeText: "Productivity Boost",
    placeholderText: "Which workflows would you like to automate..."
  },
  "Email Marketing": {
    description: "Turn subscribers into loyal customers with strategic campaigns",
    buttonText: "Grow My Email List",
    badgeText: "Email Marketing Success",
    placeholderText: "Tell us about your audience and marketing goals..."
  },
  "Lead Generation": {
    description: "Fill your pipeline with qualified prospects ready to buy",
    buttonText: "Generate More Leads",
    badgeText: "Lead Generation Experts",
    placeholderText: "What type of leads are you looking to generate..."
  },
  "PPC Marketing": {
    description: "Maximize your ad spend with campaigns that actually convert",
    buttonText: "Optimize My Ads",
    badgeText: "PPC Optimization",
    placeholderText: "Tell us about your current advertising challenges..."
  },
  "SEO": {
    description: "Dominate search results and drive organic traffic that converts",
    buttonText: "Boost My Rankings",
    badgeText: "SEO Excellence",
    placeholderText: "What keywords do you want to rank for..."
  },
  "CRO Testing": {
    description: "Optimize conversions with data-driven testing and insights",
    buttonText: "Increase My Conversions",
    badgeText: "Conversion Optimization",
    placeholderText: "What areas of your site would you like to optimize..."
  },
  "E-commerce": {
    description: "Build online stores that drive sales and customer loyalty",
    buttonText: "Grow My Online Store",
    badgeText: "E-commerce Growth",
    placeholderText: "Tell us about your e-commerce goals and challenges..."
  },
  "NoCode Development": {
    description: "Launch your product faster with enterprise-grade NoCode solutions",
    buttonText: "Start My Project",
    badgeText: "Rapid Development",
    placeholderText: "What would you like to build with NoCode..."
  }
};

export const ServiceCTA: React.FC<ServiceCTAProps> = ({
  serviceName,
  description,
  buttonText,
  badgeText,
  placeholderText,
  source
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    message: ""
  });
  const { toast } = useToast();

  // Get service-specific config or use props
  const config = serviceConfigs[serviceName as keyof typeof serviceConfigs];
  const finalDescription = description || config?.description || "Transform your business with our expert services";
  const finalButtonText = buttonText || config?.buttonText || "Get Started Today";
  const finalBadgeText = badgeText || config?.badgeText || "Expert Solutions";
  const finalPlaceholderText = placeholderText || config?.placeholderText || "Tell us about your project...";
  const finalSource = source || serviceName.toLowerCase().replace(/\s+/g, '-');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            company: formData.website,
            message: formData.message,
            source: finalSource
          }
        ]);

      if (error) throw error;

      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you within 24 hours.",
      });

      setFormData({ name: "", email: "", website: "", message: "" });
    } catch (error) {
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section className="py-12 px-4 relative overflow-hidden bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
      {/* Simplified background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl opacity-50"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-purple-400/10 rounded-full blur-3xl opacity-50"></div>
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <motion.div variants={itemVariants} className="mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              {finalBadgeText}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              {finalDescription}
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div variants={itemVariants}>
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                />
              </motion.div>
              <motion.div variants={itemVariants}>
                <input
                  type="email"
                  placeholder="Your Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
                />
              </motion.div>
            </div>
            <motion.div variants={itemVariants}>
              <input
                type="url"
                placeholder="Your Website (Optional)"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <textarea
                placeholder={finalPlaceholderText}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200 resize-none"
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </div>
                ) : (
                  finalButtonText
                )}
              </Button>
            </motion.div>
          </form>

          <motion.div 
            variants={itemVariants}
            className="mt-8 pt-6 border-t border-white/20"
          >
            <div className="flex flex-wrap justify-center gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                Free Consultation
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                24hr Response Time
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                No Commitment Required
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};